/**
 * 「我的」· 听书音色设置（App 独占）
 * 从设备系统 TTS 引擎拉取可用音色供选择，选择后立即试听；持久化在 tts-store（localStorage）。
 * Web/无原生引擎时不渲染（由父级 IS_APP 门控）。
 */
import { useEffect, useMemo, useState } from 'react';
import { useTtsStore } from '../state/tts-store';
import type { TtsVoice } from '@shared/api/tts';

function isZh(v: TtsVoice): boolean {
  const l = (v.locale || '').toLowerCase();
  return l.startsWith('zh') || l.startsWith('cmn') || l.startsWith('yue');
}

function langLabel(locale: string): string {
  const l = (locale || '').toLowerCase();
  if (!l) return '未知';
  if (l.startsWith('yue') || l.includes('hk') || l.includes('tw') || l.includes('hant')) return '中文（粤/繁）';
  if (l.startsWith('zh') || l.startsWith('cmn')) return '中文';
  if (l.startsWith('en')) return '英语';
  if (l.startsWith('ja')) return '日语';
  if (l.startsWith('ko')) return '韩语';
  if (l.startsWith('fr')) return '法语';
  if (l.startsWith('de')) return '德语';
  if (l.startsWith('es')) return '西语';
  if (l.startsWith('ru')) return '俄语';
  return locale;
}

/** 音色联合键（name+locale+quality），name 单独不唯一 */
function keyOf(v: { name: string; locale: string; quality?: number }): string {
  return `${v.name}|${v.locale}|${v.quality ?? -1}`;
}

export default function TtsVoiceSetting() {
  const voices = useTtsStore((s) => s.voices);
  const voice = useTtsStore((s) => s.voice);
  const voicesLoaded = useTtsStore((s) => s.voicesLoaded);
  const previewing = useTtsStore((s) => s.previewing);
  const supported = useTtsStore((s) => s.supported);
  const engine = useTtsStore((s) => s.engine);
  const engines = useTtsStore((s) => s.engines);
  const refreshVoices = useTtsStore((s) => s.refreshVoices);
  const refreshEngines = useTtsStore((s) => s.refreshEngines);
  const setEngine = useTtsStore((s) => s.setEngine);
  const setVoice = useTtsStore((s) => s.setVoice);
  const preview = useTtsStore((s) => s.preview);

  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let alive = true;
    void Promise.all([Promise.resolve(refreshEngines()), Promise.resolve(refreshVoices())]).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [refreshEngines, refreshVoices]);

  // 去重（按联合键）+ 排序：中文优先 → 默认在前 → locale 字典序
  const options = useMemo(() => {
    const seen = new Set<string>();
    const uniq: TtsVoice[] = [];
    for (const v of voices) {
      const k = keyOf(v);
      if (seen.has(k)) continue;
      seen.add(k);
      uniq.push(v);
    }
    return uniq.sort((a, b) => {
      const az = isZh(a) ? 0 : 1;
      const bz = isZh(b) ? 0 : 1;
      if (az !== bz) return az - bz;
      if (!!b.default !== !!a.default) return a.default ? -1 : 1;
      return (a.locale || '').localeCompare(b.locale || '');
    });
  }, [voices]);

  const labelOf = (v: TtsVoice) => `${langLabel(v.locale)} · ${v.locale || v.name}${v.default ? '（默认）' : ''}`;

  const onPick = (key: string) => {
    if (!key) {
      setVoice(null);
      void preview(null); // 回到默认并试听
      return;
    }
    const v = options.find((o) => keyOf(o) === key);
    if (!v) return;
    const ref = { name: v.name, locale: v.locale, quality: v.quality };
    setVoice(ref);
    void preview(ref); // 选择即试听
  };

  const zhCount = options.filter(isZh).length;

  // 引擎选项：默认（系统首选）在前，其余按显示名；当前默认引擎标（默认）
  const onEngineChange = async (pkg: string) => {
    setSwitching(true);
    try {
      await setEngine(pkg || null);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="rounded-lg bg-white px-4 py-3 shadow">
      <div className="font-medium text-slate-800">听书音色</div>
      <div className="mt-0.5 text-xs text-slate-400">不同引擎音色差异大；先选引擎，再选音色。</div>

      {/* 引擎选择 */}
      <div className="mt-3 flex items-center gap-2">
        <span className="w-10 shrink-0 text-sm text-slate-500">引擎</span>
        <select
          value={engine || ''}
          disabled={loading || switching}
          onChange={(e) => void onEngineChange(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-500 disabled:opacity-50"
        >
          <option value="">{loading ? '加载中…' : '默认（系统首选）'}</option>
          {engines.map((en) => (
            <option key={en.package} value={en.package}>
              {en.name}
              {en.default ? '（默认）' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* 音色选择 */}
      <div className="mt-2 flex items-center gap-2">
        <span className="w-10 shrink-0 text-sm text-slate-500">音色</span>
        <select
          value={voice ? keyOf(voice) : ''}
          disabled={loading || switching}
          onChange={(e) => onPick(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-500 disabled:opacity-50"
        >
          <option value="">{switching ? '切换中…' : '默认（跟随引擎）'}</option>
          {options.map((v) => (
            <option key={keyOf(v)} value={keyOf(v)}>
              {labelOf(v)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void preview()}
          disabled={loading || switching || previewing}
          className="shrink-0 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
        >
          {previewing ? '试听中…' : '试听'}
        </button>
      </div>

      {supported === false ? (
        <div className="mt-2 text-xs text-red-500">未检测到可用的中文语音引擎，请在系统设置安装/启用 TTS 引擎后重试。</div>
      ) : (
        voicesLoaded &&
        options.length === 0 && (
          <div className="mt-2 text-xs text-slate-400">当前设备 TTS 引擎未提供可选音色，将使用系统默认音色。</div>
        )
      )}
      {options.length > 0 && zhCount === 0 && (
        <div className="mt-2 text-xs text-amber-600">注意：该引擎未提供中文音色，中文内容可能朗读不准确。</div>
      )}
    </div>
  );
}
